// pages/module-pages/support/audit-logs/AuditLogSearchPage.tsx
import { useState, FormEvent } from "react";
import useAuditLogs from "../../../../hooks/support/auditLogs/useAuditLogs";
import AuditLogsList from "../../../../components/features/support/auditLogs/AuditLogsList";

export default function AuditLogSearchPage() {
  const [emailInput, setEmailInput] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const { logs, searchedEmail, loading, error, pagination, searchByEmail, clearResults } =
    useAuditLogs();

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    searchByEmail(trimmed, 1, methodFilter ? { method: methodFilter } : undefined);
  };

  const handleClear = () => {
    setEmailInput("");
    setMethodFilter("");
    clearResults();
  };

  const handlePageChange = (newPage: number) => {
    searchByEmail(searchedEmail, newPage, methodFilter ? { method: methodFilter } : undefined);
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.perPage));

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-800">Traçabilité utilisateur</h1>
        <p className="text-sm text-gray-500">
          Recherchez l'historique complet des actions d'un utilisateur à partir de son adresse email.
        </p>
      </div>

      <form
        onSubmit={handleSearch}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
      >
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
            Email de l'utilisateur
          </label>
          <input
            type="email"
            required
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="exemple@izilifepay.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="w-full sm:w-40">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
            Type d'action
          </label>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Toutes</option>
            <option value="GET">Connexions / Consultations</option>
            <option value="POST">Créations</option>
            <option value="PUT">Modifications</option>
            <option value="PATCH">Modifications partielles</option>
            <option value="DELETE">Suppressions</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
          >
            {loading ? "Recherche..." : "Rechercher"}
          </button>
          {searchedEmail && (
            <button
              type="button"
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 whitespace-nowrap"
            >
              Effacer
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {searchedEmail && !error && (
        <>
          <p className="text-sm text-gray-600">
            <span className="font-medium">{pagination.total}</span> événement
            {pagination.total > 1 ? "s" : ""} trouvé{pagination.total > 1 ? "s" : ""} pour{" "}
            <span className="font-medium">{searchedEmail}</span>
          </p>

          <AuditLogsList logs={logs} />

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1 || loading}
                className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-500">
                Page {pagination.currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= totalPages || loading}
                className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
